import { createContext, type PropsWithChildren, useCallback } from "react"
import { Checkbox, type CheckboxProps, ErrorMessage } from "@octopus-energy/coral/atoms"
import { useSafeContext } from "@oefr/hooks"
import { useField } from "formik"

type CheckboxOptionValue = NonNullable<NonNullable<CheckboxProps["inputProps"]>["value"]>

type CheckboxGroupContextValue<TValue extends CheckboxOptionValue> = {
    selectedValues: TValue[]
    toggle: (value: TValue) => void
    onBlur: CheckboxProps["onBlur"]
    error: boolean
}

type FormikCheckboxGroupOptionProps<TValue extends CheckboxOptionValue> = Omit<
    CheckboxProps,
    "checked" | "onChange" | "onBlur" | "error"
> & {
    value: TValue
}

type FormikCheckboxGroupProps<TValue extends CheckboxOptionValue> = PropsWithChildren<{
    name: string
    validate?: (value: TValue[]) => string | undefined
}>

export function createFormikCheckboxGroup<TValue extends CheckboxOptionValue>() {
    const CheckboxGroupContext = createContext<CheckboxGroupContextValue<TValue> | null>(null)

    function FormikCheckboxGroup({ children, ...fieldProps }: FormikCheckboxGroupProps<TValue>) {
        const [field, meta, helpers] = useField<TValue[]>(fieldProps)

        const toggle = useCallback(
            (value: TValue) => {
                const next = field.value.includes(value)
                    ? field.value.filter((v) => v !== value)
                    : [...field.value, value]

                void helpers.setValue(next)
            },
            [field.value, helpers]
        )

        const onBlur = useCallback(() => {
            void helpers.setTouched(true)
        }, [helpers])

        return (
            <CheckboxGroupContext.Provider
                value={{
                    selectedValues: field.value,
                    toggle,
                    onBlur,
                    error: meta.touched && !!meta.error,
                }}
            >
                {children}
                {meta.touched && meta.error && <ErrorMessage message={meta.error} />}
            </CheckboxGroupContext.Provider>
        )
    }

    function FormikCheckboxGroupOption({
        value,
        ...checkboxProps
    }: FormikCheckboxGroupOptionProps<TValue>) {
        const { selectedValues, toggle, onBlur, error } = useSafeContext(CheckboxGroupContext)

        const handleChange = useCallback(() => {
            toggle(value)
        }, [toggle, value])

        return (
            <Checkbox
                {...checkboxProps}
                checked={selectedValues.includes(value)}
                onChange={handleChange}
                onBlur={onBlur}
                error={error}
            />
        )
    }

    return { FormikCheckboxGroup, FormikCheckboxGroupOption }
}
